import React, { useState } from 'react';
import { Card, Carousel, Icon, message } from 'antd';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';

import { numberWithCommas } from 'utils/helpers/helpers';
import { buildAwardsHostProfileRoute, buildHostProfileRoute } from 'utils/routes';

import NoImagePlaceholder from './images/no-image-placeholder.jpg';

const { Meta } = Card;

const CardTitle = styled.span`
  text-transform: capitalize;
`;

const HostNameLink = styled(Link)`
  color: ${props => props.theme.color.black};
`;

const CoverPhotoContainer = styled.div`
  overflow: hidden;
  text-align: center;
  height: 200px;
  line-height: 200px;
`;

const CoverPhoto = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 200px;
  margin: auto;
  vertical-align: middle;
`;

const DescriptionTitle = styled.p`
  color: ${props => props.theme.color.black};
  font-weight: 700;
  margin-bottom: 0px;
`;

const CarouselContainer = styled.div`
  text-align: center;
  height: 200px;
  line-height: 200px;
  background: ${props => (props.isPhotosEmpty ? props.theme.color.lightestGrey : props.theme.color.darkBlack)};
  overflow: hidden;
`;

const CarouselPhoto = styled.img`
  display: inline-block !important;
  width: 100%;
  height: auto;
  margin: auto;
  max-width: 100%;
  max-height: 165px;
  vertical-align: middle;

  &:hover {
    cursor: pointer;
  }
`;

const MediaActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 -24px 24px -24px;
  background: ${props => props.theme.color.white};
  border-top: 1px solid ${props => props.theme.color.borderColor};
  border-bottom: 1px solid ${props => props.theme.color.borderColor};
`;

const VoteContainer = styled.div`
  display: flex;
  padding: 12px;
  line-height: 24px;
`;

const VoteIcon = styled(Icon)`
  font-size: 24px;
  color: #ffc20e;
`;

const VoteNumberText = styled.span`
  line-height: 24px;
  margin-left: 4px;
`;

const ShareContainer = styled.div`
  display: flex;
  line-height: 24px;
  border-left: 1px solid ${props => props.theme.color.borderColor};
  padding: 12px;
`;

const ShareIcon = styled(Icon)`
  font-size: 24px;

  &:hover {
    cursor: pointer;
    color: ${props => props.theme.color.secondary};
  }
`;

const getVoteCountForDisplay = (isJustVoted, voteCount) => {
  const finalVoteCount = isJustVoted ? voteCount + 1 : voteCount;
  const voteText = finalVoteCount > 1 ? 'votes' : 'vote';
  return `${numberWithCommas(finalVoteCount, 0)} ${voteText}`;
};

const getErrorPromptMsg = type => `You've reached your vote limit for this ${type}. Come back and vote again tomorrow.`;

// NOTE: document.execCommand only support input copy
const handleOnCopyLinkClick = (type, hostId = '', isFromAwardsPage) => {
  const link = `${window.location.origin}${isFromAwardsPage ? buildAwardsHostProfileRoute(hostId) : buildHostProfileRoute(hostId)}`;
  const tempInput = document.createElement('input');
  document.body.appendChild(tempInput);
  tempInput.value = link;
  tempInput.select();
  document.execCommand('copy');
  document.body.removeChild(tempInput);
  const messageToDisplay =
    type === 'unit' ? "You've copied the link to this unit's host profile page." : "You've copied the link to this host profile page.";
  message.success(messageToDisplay);
};

const UnitCard = ({ unit, onClickUnitPhoto, isVoted, onVote }) => {
  const [isJustVoted, setIsJustVoted] = useState(false);
  const voteCountText = getVoteCountForDisplay(isJustVoted, unit.voteCount);

  return (
    <Card
      style={{ height: '100%' }}
      title={<CardTitle>{unit.name}</CardTitle>}
      cover={
        <CoverPhotoContainer>
          <Carousel>
            {unit.photos && unit.photos.length > 0 ? (
              unit.photos.map(photo => (
                <div key={photo}>
                  <CarouselContainer>
                    <CarouselPhoto alt={`${unit.name} photo`} src={photo} onClick={() => onClickUnitPhoto(unit.photos)} />
                  </CarouselContainer>
                </div>
              ))
            ) : (
              <div>
                <CarouselContainer isPhotosEmpty>
                  <CarouselPhoto alt={`no unit image`} src={NoImagePlaceholder} />
                </CarouselContainer>
              </div>
            )}
          </Carousel>
        </CoverPhotoContainer>
      }
    >
      <MediaActionContainer>
        <VoteContainer>
          <VoteIcon
            type="heart"
            theme={isVoted ? 'filled' : 'outlined'}
            onClick={() => {
              if (!isVoted) {
                onVote();
                setIsJustVoted(true);
              } else {
                message.warning(getErrorPromptMsg('unit'));
              }
            }}
          />
          <VoteNumberText>{voteCountText}</VoteNumberText>
        </VoteContainer>
        <ShareContainer onClick={() => handleOnCopyLinkClick('unit', unit.host.id, !!onVote)}>
          <ShareIcon type="link" />
        </ShareContainer>
      </MediaActionContainer>

      <Meta
        description={
          <div>
            <DescriptionTitle>Managed by</DescriptionTitle>
            <p>
              <HostNameLink to={buildAwardsHostProfileRoute(unit.host.id)}>{unit.host.name}</HostNameLink>
            </p>
          </div>
        }
      />
    </Card>
  );
};

const HostCard = ({ host, isVoted, onVote }) => {
  const [isJustVoted, setIsJustVoted] = useState(false);
  const voteCountText = getVoteCountForDisplay(isJustVoted, host.voteCount);

  return (
    <Card
      style={{ height: '100%' }}
      title={
        <CardTitle>
          <HostNameLink to={onVote ? buildAwardsHostProfileRoute(host._id) : buildHostProfileRoute(host._id)}>{host.name}</HostNameLink>
        </CardTitle>
      }
      cover={
        <CoverPhotoContainer>
          <HostNameLink to={onVote ? buildAwardsHostProfileRoute(host._id) : buildHostProfileRoute(host._id)}>
            <CoverPhoto alt={host.name} src={host.logo || NoImagePlaceholder} />
          </HostNameLink>
        </CoverPhotoContainer>
      }
    >
      {!!onVote && <MediaActionContainer>
        <VoteContainer>
          <VoteIcon
            type="heart"
            theme={isVoted ? 'filled' : 'outlined'}
            onClick={() => {
              if (!isVoted) {
                onVote();
                setIsJustVoted(true);
              } else {
                message.warning(getErrorPromptMsg('host'));
              }
            }}
          />
          <VoteNumberText>{voteCountText}</VoteNumberText>
        </VoteContainer>
        <ShareContainer onClick={() => handleOnCopyLinkClick('host', host._id, !!onVote)}>
          <ShareIcon type="link" />
        </ShareContainer>
      </MediaActionContainer>}

      <Meta
        description={
          <div>
            <DescriptionTitle>Covered Area(s)</DescriptionTitle>
            <p>{host.coveredAreas.join(', ')}</p>
            <DescriptionTitle>Total Units</DescriptionTitle>
            <p>{host.unitsOnHand}</p>
            <DescriptionTitle>{`About ${host.name}`}</DescriptionTitle>
            <p>{host.briefDescription || '-'}</p>
          </div>
        }
      />
    </Card>
  );
};

const CampaignCard = ({ type, data, onClickUnitPhoto, isVoted, onVote }) => {
  if (type === 'host') {
    return <HostCard host={data} isVoted={isVoted} onVote={onVote} />;
  } else if (type === 'unit') {
    return <UnitCard unit={data} onClickUnitPhoto={onClickUnitPhoto} isVoted={isVoted} onVote={onVote} />;
  } else {
    return <p>{`Type: ${type} is not matched.`}</p>;
  }
};

export default CampaignCard;
